import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useNotifications } from "@/lib/hooks/useNotifications";
import type { Notification, NotificationType } from "@/redux/slices/notificationsSlice";
import Dropdown from "@/components/ui/dropdown/Dropdown";
import Tooltip from "../tooltip/Tooltip";

// Format time ago utility
const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export default function NotificationBell() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const {
    notifications,
    // filteredNotifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refresh,
  } = useNotifications({
    autoFetch: true,
    playSound: false, // Don't play sound in dropdown
    pollInterval: 30000, // Poll every 30 seconds
  });

  // Get recent notifications for dropdown (prioritize unread, then recent)
  const recentNotifications = notifications
    .filter((n) => !n.is_read)
    .concat(notifications.filter((n) => n.is_read))
    .slice(0, 10);

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (isOpen && notifications.length === 0 && !loading) {
      refresh();
    }
  }, [isOpen, notifications.length, loading, refresh]);

  const getNotificationIcon = (type: NotificationType) => {
    const iconConfig: Record<NotificationType, { bg: string; color: string; path: string }> = {
      CALL: {
        bg: "bg-blue-100",
        color: "text-blue-600",
        path: "M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z",
      },
      VISITOR: {
        bg: "bg-green-100",
        color: "text-green-600",
        path: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z",
      },
      TEAM: {
        bg: "bg-orange-100",
        color: "text-orange-600",
        path: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z",
      },
      MEETING: {
        bg: "bg-purple-100",
        color: "text-purple-600",
        path: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
      },
      SYSTEM: {
        bg: "bg-gray-100",
        color: "text-gray-600",
        path: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z",
      },
      ALERT: {
        bg: "bg-red-100",
        color: "text-red-600",
        path: "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9",
      },
      PAYMENT: {
        bg: "bg-emerald-100",
        color: "text-emerald-600",
        path: "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z",
      },
    };

    const config = iconConfig[type] || iconConfig.SYSTEM;

    return (
      <div
        className={`size-10 rounded-full ${config.bg} flex items-center justify-center shrink-0`}
      >
        <svg
          className={`size-5 ${config.color}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d={config.path} />
        </svg>
      </div>
    );
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    setIsOpen(false);

    // Navigate to action URL if available
    if (notification.action_url) {
      if (notification.action_url.startsWith("http")) {
        window.open(notification.action_url, "_blank");
      } else {
        navigate(notification.action_url);
      }
    } else {
      // Default: navigate to notifications page
      navigate("/notifications");
    }
  };

  return (
    <Dropdown
      position="bottom-left"
      open={isOpen}
      triggerClassName="rounded-full"
      onOpenChange={setIsOpen}
      gap={0}
      dropDownClass="!p-0 !bg-card !border-border !rounded-xl w-96 max-h-[80vh] flex flex-col shadow-xl overflow-hidden"
      trigger={
        <Tooltip content={isOpen ? null : "Notifications"}>
          <button
            type="button"
            className="relative size-11 rounded-full bg-light-1 flex items-center justify-center text-gray-400 hover:bg-light-2 transition-colors"
            aria-label="Notifications"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="size-4.5 text-gray-500"
            >
              <path
                d="M18.7491 9.70957V9.00497C18.7491 5.13623 15.7274 2 12 2C8.27256 2 5.25087 5.13623 5.25087 9.00497V9.70957C5.25087 10.5552 5.00972 11.3818 4.5578 12.0854L3.45036 13.8095C2.43882 15.3843 3.21105 17.5249 4.97036 18.0229C9.57274 19.3257 14.4273 19.3257 19.0296 18.0229C20.789 17.5249 21.5612 15.3843 20.5496 13.8095L19.4422 12.0854C18.9903 11.3818 18.7491 10.5552 18.7491 9.70957Z"
                stroke="currentColor"
                strokeWidth="1.6"
              />
              <path
                d="M7.5 19C8.15503 20.7478 9.92246 22 12 22C14.0775 22 15.845 20.7478 16.5 19"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-5 h-5 bg-sec text-white text-[10px] font-bold rounded-full flex-center shadow-sm">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>
        </Tooltip>
      }
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-light-1/50 rounded-t-xl shrink-0">
        <h3 className="text-lg font-semibold text-text">Notifications</h3>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="text-sm text-gray-500 hover:opacity-90 font-medium"
            >
              Mark all read
            </button>
          )}
          <button
            onClick={refresh}
            className="p-1.5 text-gray-500 hover:text-text hover:bg-white/10 rounded-lg transition-colors"
            title="Refresh"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Notifications List – max-h so overflow triggers; notification-list-scroll for visible scrollbar */}
      <div className="flex-1 min-h-0 max-h-[min(60vh,400px)] sm-scroll">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-border border-t-pri rounded-full animate-spin" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-12 px-4">
            <div className="w-16 h-16 mx-auto mb-4 bg-light rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
            </div>
            <p className="text-text font-medium">No notifications yet</p>
            <p className="text-gray-500 text-sm mt-1">We'll notify you when something happens</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {recentNotifications.map((notification) => (
              <div
                key={notification.id}
                className={`px-4 py-3 hover:bg-light-1/80 transition-colors cursor-pointer group ${
                  !notification.is_read ? "bg-light-2" : ""
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex items-start gap-3">
                  {getNotificationIcon(notification.type)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <p
                          className={`text-sm ${
                            !notification.is_read ? "font-semibold" : "font-medium"
                          } text-text`}
                        >
                          {notification.title}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-600 mt-1.5">
                          {formatTimeAgo(notification.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {!notification.is_read && (
                      <span className="w-2 h-2 bg-sec rounded-full shrink-0" />
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification(notification.id);
                      }}
                      className="p-1 rounded hover:bg-light-2 transition-colors"
                      aria-label="Delete notification"
                    >
                      <svg
                        className="w-4 h-4 text-gray-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {recentNotifications.length > 0 && (
        <div className="px-4 py-3 border-t border-border bg-light-1/50 rounded-b-xl shrink-0">
          <Link
            to="/notifications"
            className="block text-center text-sm text-pri hover:opacity-90 font-medium"
            onClick={() => setIsOpen(false)}
          >
            View all notifications
          </Link>
        </div>
      )}
    </Dropdown>
  );
}
